import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * TOT — "show one row, reveal the rest" wrapper for any CSS-grid list.
 *
 * The COLLAPSED state is pure CSS (see `.tot-reveal` rules in custom.css): the grid
 * gets `grid-auto-rows: 0` so every row past `--tot-reveal-rows` clips to zero height.
 * That means the first paint is already correct — no flash of the full grid — and the
 * clamp follows the grid's own responsive/container-query column count for free.
 *
 * This component only handles the parts CSS can't:
 *   1. toggling `data-expanded` and animating the height change,
 *   2. hiding the button when there is nothing to reveal at the current column count,
 *   3. marking clipped items `inert` so keyboard focus can't land on invisible cards.
 *
 * @typedef {Object} TotRevealRefs
 * @property {HTMLElement} revealButton - The show all / show less toggle
 * @property {HTMLElement} revealContent - Wrapper around the grid; the element we animate
 *
 * @extends {Component<TotRevealRefs>}
 */
class TotRevealComponent extends Component {
  requiredRefs = ['revealButton', 'revealContent'];

  /** Grids this component knows how to clamp (single-span items only). */
  static GRID_SELECTOR = '.resource-list--grid, .category-grid__grid';

  /** @type {Animation | undefined} */
  #animation;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @constant {number} */
  #animationSpeed = 320;

  /** @returns {HTMLElement | null} */
  get #grid() {
    return this.refs.revealContent?.querySelector(TotRevealComponent.GRID_SELECTOR) ?? null;
  }

  /** @returns {boolean} */
  get expanded() {
    return this.dataset.expanded === 'true';
  }

  /** Rows kept visible while collapsed. Mirrors the `--tot-reveal-rows` CSS var. */
  get #rows() {
    return Number(this.dataset.rows) || 1;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#sync();

    // Column count changes with the viewport AND with the section's container
    // queries, and row height changes as product images load. One observer on the
    // grid catches all three.
    const grid = this.#grid;
    if (grid) {
      this.#resizeObserver = new ResizeObserver(this.#onResize);
      this.#resizeObserver.observe(grid);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#animation?.cancel();
  }

  #onResize = debounce(() => this.#sync(), 150);

  /**
   * Number of columns the grid is currently rendering.
   * Reads the *used* track list, so container queries and breakpoints are already
   * resolved. Returns 0 when the grid isn't rendered — e.g. the desktop grid on
   * mobile when the section falls back to a carousel.
   *
   * @returns {number}
   */
  #columnCount() {
    const grid = this.#grid;
    if (!grid || grid.getClientRects().length === 0) return 0;

    const tracks = getComputedStyle(grid).gridTemplateColumns;
    if (!tracks || tracks === 'none') return 0;

    return tracks.split(' ').filter(Boolean).length;
  }

  /**
   * Reconciles button visibility and `inert` flags with the current column count.
   */
  #sync = () => {
    const grid = this.#grid;
    const columns = this.#columnCount();
    const items = grid ? /** @type {HTMLElement[]} */ ([...grid.children]) : [];
    const visibleCount = columns * this.#rows;
    const hasOverflow = columns > 0 && items.length > visibleCount;

    this.refs.revealButton.hidden = !hasOverflow;
    this.toggleAttribute('data-has-overflow', hasOverflow);

    const clipping = hasOverflow && !this.expanded;
    items.forEach((item, index) => item.toggleAttribute('inert', clipping && index >= visibleCount));
  };

  /**
   * Toggles between the clamped and full grid.
   *
   * @param {Event} event - The click event
   */
  toggle = (event) => {
    event.preventDefault();

    const { revealContent, revealButton } = this.refs;
    const startHeight = revealContent.offsetHeight;

    this.dataset.expanded = this.expanded ? 'false' : 'true';
    revealButton.setAttribute('aria-expanded', String(this.expanded));
    this.#sync();

    // Read back after the attribute flip so the browser has re-laid out the grid.
    this.#animateHeight(revealContent, startHeight, revealContent.offsetHeight);

    if (!this.expanded) this.#keepInView();
  };

  /**
   * @param {HTMLElement} element
   * @param {number} startHeight
   * @param {number} endHeight
   */
  #animateHeight(element, startHeight, endHeight) {
    this.#animation?.cancel();

    if (prefersReducedMotion()) return;

    element.style.overflow = 'hidden';
    this.#animation = element.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => element.style.removeProperty('overflow');
  }

  /**
   * Collapsing can leave the button far below the fold — pull the section back into
   * view so the click doesn't feel like the page jumped away.
   */
  #keepInView() {
    if (this.getBoundingClientRect().top >= 0) return;

    (this.closest('.section') ?? this).scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }
}

if (!customElements.get('tot-reveal')) {
  customElements.define('tot-reveal', TotRevealComponent);
}
